import React, { useRef, useCallback, useEffect, useState } from 'react'
import {
  Play, Pause, SkipBack, SkipForward, Volume2, VolumeX,
  Volume1, Maximize, Minimize, PanelRight, PanelRightClose,
  Cast,
} from 'lucide-react'
import gsap from 'gsap'
import { useGSAP } from '@gsap/react'
import { usePlaybackStore } from '../store'
import { formatDuration, clamp } from '../utils'

gsap.registerPlugin(useGSAP)

interface ControlsProps {
  onSeek: (seconds: number) => void
  onTogglePlay: () => void
  onNext: () => void
  onPrevious: () => void
  onToggleFullscreen: () => void
  isBroadcastMode?: boolean
  previewSource: string | null
}

export function VideoControls({
  onTogglePlay,
  onNext,
  onPrevious,
  onToggleFullscreen,
  onSeek,
  isBroadcastMode = false,
  previewSource,
}: ControlsProps) {
  const {
    status, position, duration, volume, muted, currentVideoId,
    isFullscreen, isSidebarOpen, isTheaterMode,
    currentPlaybackRole,
    bumperSkipAllowed, skipBumper,
    setVolume, setMuted, setSidebarOpen,
  } = usePlaybackStore()

  const progressRef = useRef<HTMLDivElement>(null)
  const progressFillRef = useRef<HTMLDivElement>(null)
  const controlsRef = useRef<HTMLDivElement>(null)
  const volumeRef = useRef<HTMLDivElement>(null)
  const volumeFillRef = useRef<HTMLDivElement>(null)
  const isDraggingRef = useRef(false)
  const previewCacheRef = useRef(new Map<number, string>())
  const previewRequestRef = useRef(new Set<number>())
  const [preview, setPreview] = useState<{ time: number; image: string | null; progress: number } | null>(null)

  const isPlaying = status === 'playing' || status === 'buffering'
  const progress = duration > 0 ? (position / duration) * 100 : 0

  const getTargetTime = useCallback((clientX: number) => {
    const bar = progressRef.current
    if (!bar || !duration) return null
    const rect = bar.getBoundingClientRect()
    return clamp((clientX - rect.left) / rect.width, 0, 1) * duration
  }, [duration])

  const loadPreviewFrame = useCallback((time: number) => {
    if (!previewSource || !currentVideoId) return
    const key = Math.round(time / 5) * 5
    const cached = previewCacheRef.current.get(key)
    if (cached) {
      setPreview((current) => current ? { ...current, image: cached } : current)
      return
    }
    if (previewRequestRef.current.has(key)) return
    previewRequestRef.current.add(key)

    const video = document.createElement('video')
    video.muted = true
    video.preload = 'auto'
    video.src = previewSource
    const cleanup = () => {
      previewRequestRef.current.delete(key)
      video.removeAttribute('src')
      video.load()
    }
    video.addEventListener('loadedmetadata', () => {
      video.currentTime = clamp(key, 0, video.duration || 0)
    }, { once: true })
    video.addEventListener('seeked', () => {
      try {
        const canvas = document.createElement('canvas')
        const scale = Math.min(1, 240 / Math.max(video.videoWidth || 1, 1))
        canvas.width = Math.max(1, Math.round(video.videoWidth * scale))
        canvas.height = Math.max(1, Math.round(video.videoHeight * scale))
        canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height)
        const image = canvas.toDataURL('image/jpeg', 0.72)
        previewCacheRef.current.set(key, image)
        while (previewCacheRef.current.size > 48) {
          previewCacheRef.current.delete(previewCacheRef.current.keys().next().value!)
        }
        setPreview((current) => current && Math.abs(current.time - key) <= 5
          ? { ...current, image }
          : current)
      } catch {
        // Some browser/provider combinations disallow canvas extraction. The
        // timestamp remains useful and the preview deliberately degrades.
      } finally {
        cleanup()
      }
    }, { once: true })
    video.addEventListener('error', cleanup, { once: true })
  }, [currentVideoId, previewSource])

  const showPreview = useCallback((clientX: number) => {
    const time = getTargetTime(clientX)
    if (time === null || !duration) return
    const progress = (time / duration) * 100
    const key = Math.round(time / 5) * 5
    setPreview({ time, image: previewCacheRef.current.get(key) ?? null, progress })
    loadPreviewFrame(time)
  }, [duration, getTargetTime, loadPreviewFrame])

  const handleProgressPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!duration) return
    isDraggingRef.current = true
    e.currentTarget.setPointerCapture(e.pointerId)
    showPreview(e.clientX)
  }, [duration, showPreview])

  const handleProgressPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (isDraggingRef.current || e.pointerType === 'mouse') showPreview(e.clientX)
  }, [showPreview])

  const handleProgressPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return
    isDraggingRef.current = false
    const time = getTargetTime(e.clientX)
    if (time !== null) onSeek(time)
    window.setTimeout(() => setPreview(null), 200)
  }, [getTargetTime, onSeek])

  useEffect(() => {
    previewCacheRef.current.clear()
    previewRequestRef.current.clear()
    setPreview(null)
  }, [currentVideoId, previewSource])

  const handleVolumeClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const bar = volumeRef.current
    if (!bar) return
    const rect = bar.getBoundingClientRect()
    const ratio = clamp((e.clientX - rect.left) / rect.width, 0, 1)
    setVolume(ratio)
    if (ratio > 0) setMuted(false)
  }, [setVolume, setMuted])

  const VolumeIcon = muted || volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2
  const volumeProgress = muted ? 0 : volume

  const { contextSafe } = useGSAP(() => {
    const mm = gsap.matchMedia()

    mm.add(
      {
        reduceMotion: '(prefers-reduced-motion: reduce)',
      },
      (context) => {
        const reduceMotion = context.conditions?.reduceMotion

        gsap.set('.js-control-fill', { transformOrigin: 'left center' })

        if (reduceMotion) {
          gsap.set('.js-controls-shell', { autoAlpha: 1, y: 0, scale: 1 })
          return
        }

        gsap.fromTo(
          '.js-controls-shell',
          { autoAlpha: 0, y: 10, scale: 0.985 },
          {
            autoAlpha: 1,
            y: 0,
            scale: 1,
            duration: 0.28,
            ease: 'power3.out',
          },
        )

        gsap.fromTo(
          '.js-control-button',
          { y: 4, autoAlpha: 0 },
          {
            y: 0,
            autoAlpha: 1,
            duration: 0.2,
            ease: 'power2.out',
            stagger: 0.025,
            delay: 0.05,
          },
        )
      },
    )

    return () => mm.revert()
  }, { scope: controlsRef })

  useEffect(() => {
    gsap.to(progressFillRef.current, {
      scaleX: clamp(progress / 100, 0, 1),
      duration: isDraggingRef.current ? 0.04 : 0.18,
      ease: 'power2.out',
      overwrite: 'auto',
    })
  }, [progress])

  useEffect(() => {
    gsap.to(volumeFillRef.current, {
      scaleX: clamp(volumeProgress, 0, 1),
      duration: 0.18,
      ease: 'power2.out',
      overwrite: 'auto',
    })
  }, [volumeProgress])

  const animatePress = contextSafe((target: HTMLElement) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    gsap.fromTo(
      target,
      { scale: 0.92 },
      {
        scale: 1,
        duration: 0.32,
        ease: 'elastic.out(1, 0.55)',
        overwrite: true,
      },
    )
  })

  return (
    <div ref={controlsRef} className="px-3 pb-3 pt-10 bg-gradient-to-t from-black/85 via-black/40 to-transparent">
      <div className="js-controls-shell mx-auto w-full max-w-4xl rounded-lg border border-border/80 bg-panel/88 px-3 py-2 shadow-lg shadow-black/30 backdrop-blur-md">
        <div className="flex items-center gap-2">
          <button
            onClick={(event) => {
              animatePress(event.currentTarget)
              onPrevious()
            }}
            className="js-control-button flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-soft transition-colors hover:bg-border hover:text-bright"
            title="Previous (P)"
          >
            <SkipBack size={16} fill="currentColor" />
          </button>

          <button
            onClick={(event) => {
              animatePress(event.currentTarget)
              onTogglePlay()
            }}
            className="js-control-button flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white text-black shadow-md shadow-black/25"
            title="Play / Pause (Space)"
          >
            {isPlaying
              ? <Pause size={19} fill="currentColor" />
              : <Play size={19} fill="currentColor" className="translate-x-0.5" />
            }
          </button>

          <button
            onClick={(event) => {
              animatePress(event.currentTarget)
              onNext()
            }}
            className="js-control-button flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-soft transition-colors hover:bg-border hover:text-bright"
            title="Next (N)"
          >
            <SkipForward size={16} fill="currentColor" />
          </button>

          {currentPlaybackRole === 'bumper' && (
            <div className="hidden flex-shrink-0 items-center gap-1 sm:flex">
              <span className="rounded bg-accent/20 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-accent">Bumper</span>
              {bumperSkipAllowed && !isBroadcastMode && (
                <button onClick={skipBumper} className="btn-ghost rounded px-2 py-1 text-[10px]" title="Skip bumper">Skip</button>
              )}
            </div>
          )}

          {isBroadcastMode && (
            <span className="hidden flex-shrink-0 items-center gap-1 rounded bg-white/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-soft sm:inline-flex">
              <Cast size={11} />
              Output
            </span>
          )}

          <span className="ml-1 hidden w-10 flex-shrink-0 font-mono text-[11px] tabular-nums text-muted sm:block">
            {formatDuration(position)}
          </span>

          <div
            ref={progressRef}
            className="progress-bar group relative h-1.5 min-w-0 flex-1 cursor-pointer touch-none"
            onPointerDown={handleProgressPointerDown}
            onPointerMove={handleProgressPointerMove}
            onPointerUp={handleProgressPointerUp}
            onPointerLeave={() => {
              if (!isDraggingRef.current) setPreview(null)
            }}
          >
            <div className="absolute inset-0 rounded-full bg-border" />
            <div
              ref={progressFillRef}
              className="js-control-fill absolute left-0 top-0 h-full w-full scale-x-0 rounded-full bg-accent"
              style={{ transformOrigin: 'left center' }}
            />
            <div
              className="absolute top-1/2 h-3 w-3 -translate-y-1/2 rounded-full bg-white shadow opacity-0 transition-opacity group-hover:opacity-100"
              style={{ left: `calc(${progress}% - 6px)` }}
            />
            {preview && (
              <div
                className="pointer-events-none absolute bottom-3 z-10 w-36 -translate-x-1/2 overflow-hidden rounded-md border border-border bg-panel p-1 shadow-xl"
                style={{ left: `clamp(72px, ${preview.progress}%, calc(100% - 72px))` }}
              >
                {preview.image ? (
                  <img src={preview.image} alt="" className="aspect-video w-full object-cover" />
                ) : (
                  <div className="flex aspect-video items-center justify-center bg-surface text-[10px] text-muted">
                    Preview unavailable
                  </div>
                )}
                <p className="px-1 pt-1 text-center font-mono text-[10px] text-soft">
                  {formatDuration(preview.time)}
                </p>
              </div>
            )}
          </div>

          <span className="hidden w-10 flex-shrink-0 text-right font-mono text-[11px] tabular-nums text-muted sm:block">
            {formatDuration(duration)}
          </span>

          <div className="ml-1 hidden items-center gap-2 md:flex">
            <button
              onClick={(event) => {
                animatePress(event.currentTarget)
                setMuted(!muted)
              }}
              className="js-control-button flex h-8 w-8 items-center justify-center rounded-md text-soft transition-colors hover:bg-border hover:text-bright"
              title="Mute (M)"
            >
              <VolumeIcon size={15} />
            </button>
            <div
              ref={volumeRef}
              className="relative h-1.5 w-24 cursor-pointer rounded-full bg-border"
              onClick={handleVolumeClick}
            >
              <div
                ref={volumeFillRef}
                className="js-control-fill absolute left-0 top-0 h-full w-full scale-x-0 rounded-full bg-accent"
                style={{ transformOrigin: 'left center' }}
              />
            </div>
            <span className="w-8 text-right font-mono text-[11px] text-muted">
              {Math.round((muted ? 0 : volume) * 100)}%
            </span>
          </div>

          {!isTheaterMode && !isBroadcastMode && (
            <button
              onClick={(event) => {
                animatePress(event.currentTarget)
                setSidebarOpen(!isSidebarOpen)
              }}
              className="js-control-button flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-soft transition-colors hover:bg-border hover:text-bright"
              title="Toggle sidebar (S)"
            >
              {isSidebarOpen ? <PanelRightClose size={15} /> : <PanelRight size={15} />}
            </button>
          )}

          <button
            onClick={(event) => {
              animatePress(event.currentTarget)
              onToggleFullscreen()
            }}
            className="js-control-button flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-soft transition-colors hover:bg-border hover:text-bright"
            title="Fullscreen (F)"
          >
            {isFullscreen ? <Minimize size={15} /> : <Maximize size={15} />}
          </button>
        </div>
      </div>
    </div>
  )
}
